import { ContentCopy, ExpandMore } from '@mui/icons-material';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Grid,
	IconButton,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { FieldConfiguration } from '../../../api/ConfigApi';
import { useConfigContext } from '../../../context/config';
import { CatchUpInvoice } from '../../../types/catchUps';
import { CurrencyCode } from '../../../types/currencyCode';
import { FieldValuesMap } from '../../../types/policy';
import { formatCurrency } from '../../../util/util';
import { StyledEmptyMessage } from '../../common';
import DefaultDatePicker from '../../form/DefaultDatePicker';
import Field from '../../policy/fields/Field';
import {
	COPY_PAYMENT__ACTION,
	NO_PAYMENTS__MESSAGE,
	PAYMENTS_LIST__TITLE,
	PAYMENT_AMOUNT__TITLE,
	PAYMENT_DATE__TITLE,
	PAYMENT_REVERSED__LABEL,
	PAYMENT__DISPLAY_ID,
	REVERSED_DATE__TITLE,
} from '../data/constants';

interface PaymentListProps {
	catchUp: CatchUpInvoice;
}

const PaymentsList = (props: PaymentListProps) => {
	const { catchUp } = props;
	const { payment } = catchUp;

	const [expanded, setExpanded] = useState([] as string[]);

	const configData = useConfigContext();
	const {
		paymentConfiguration: { paymentConfiguration },
	} = configData;
	const { fields } = paymentConfiguration;

	const noPaymentsMessage = () => {
		return <StyledEmptyMessage>{NO_PAYMENTS__MESSAGE}</StyledEmptyMessage>;
	};

	const renderFields = (
		fieldConfigs: FieldConfiguration[],
		fieldValuesMap: FieldValuesMap,
	) => {
		return fieldConfigs.map((f, index) => {
			const value = fieldValuesMap[f.name] || [''];
			return (
				<Grid item xs={f.width ? f.width : 6} key={index}>
					<Field
						key={f.name}
						field={f}
						value={value}
						isDisabled
						allValues={null}
						updateOnChange
						updateFieldValue={null}
					/>
				</Grid>
			);
		});
	};

	const handleChange = (panel: any) => (event: any, isExpanded: boolean) => {
		const expandedAccordions = [...expanded];
		if (isExpanded) {
			expandedAccordions.push(panel);
		} else {
			const i = expanded.indexOf(panel);
			expandedAccordions.splice(i, 1);
		}
		setExpanded(expandedAccordions);
	};

	return (
		<>
			<div style={{ margin: '16px 0' }}>{PAYMENTS_LIST__TITLE}</div>
			{payment ? (
				<Accordion
					key={payment.displayId}
					expanded={expanded.includes(payment.displayId)}
					onChange={handleChange(payment.displayId)}
				>
					<AccordionSummary expandIcon={<ExpandMore />} id={payment.displayId}>
						<Typography sx={{ width: '50%' }}>
							{PAYMENT__DISPLAY_ID(payment.displayId)}
							<Tooltip title={COPY_PAYMENT__ACTION} arrow placement='top'>
								<IconButton
									color='info'
									aria-label={COPY_PAYMENT__ACTION}
									onClick={() => navigator.clipboard.writeText(payment.locator)}
									size='small'
									sx={{ marginLeft: '6px', marginBottom: '2px' }}
								>
									<ContentCopy sx={{ fontSize: 18 }} color='primary' />
								</IconButton>
							</Tooltip>
						</Typography>
						{payment.reversedTimestamp ? (
							<Typography>{PAYMENT_REVERSED__LABEL}</Typography>
						) : null}
					</AccordionSummary>
					<AccordionDetails sx={{ pb: 2 }}>
						<Grid container spacing={2} sx={{ pb: 2 }}>
							<Grid item xs={4}>
								<DefaultDatePicker
									value={new Date(parseInt(payment.postedTimestamp, 10))}
									onChange={null}
									label={PAYMENT_DATE__TITLE}
									style={{
										width: '100%',
										marginTop: '16px',
										marginRight: '4%',
									}}
									disabled
									showCalendar
									id={`${payment.displayId}-payment-date`}
								/>
							</Grid>
							<Grid item xs={4}>
								<TextField
									id={`${payment.displayId}-payment-amount`}
									sx={{ width: '100%', mt: '16px', mr: '4%' }}
									label={PAYMENT_AMOUNT__TITLE}
									value={formatCurrency(
										parseFloat(payment.amount),
										catchUp.totalDueCurrency as CurrencyCode,
									)}
									disabled
								/>
							</Grid>
							{payment.reversedTimestamp ? (
								<Grid item xs={4}>
									<DefaultDatePicker
										value={new Date(parseInt(payment.reversedTimestamp, 10))}
										onChange={null}
										label={REVERSED_DATE__TITLE}
										style={{ width: '100%', marginTop: '16px' }}
										disabled
										showCalendar
										id={`${payment.displayId}-reversed-date`}
									/>
								</Grid>
							) : null}
						</Grid>
						<Grid container spacing={3}>
							{renderFields(fields, payment.fieldValues)}
						</Grid>
					</AccordionDetails>
				</Accordion>
			) : (
				noPaymentsMessage()
			)}
		</>
	);
};

export default PaymentsList;
